import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase } from './supabaseClient'
import SchoolDetail from './SchoolDetail'
import './Pipeline.css'

const STATUSES = ['Researching', 'Contacted', 'Visited', 'Offer Received', 'Committed']

const MY_INTEREST_OPTIONS = ['Dream School', 'High Interest', 'Interest']
const CONTACT_LEVEL_OPTIONS = [
  'No Official Contact', 'Camp Invite', 'Coach Response - Email',
  'Coach Response - Text', 'Coach Response - Call', 'Third Party Contact', 'In-Person Contact',
]
const MILES_OPTIONS = [
  { label: 'Under 100 miles', max: 100, min: null },
  { label: 'Under 250 miles', max: 250, min: null },
  { label: 'Under 500 miles', max: 500, min: null },
  { label: '500+ miles',      max: null, min: 500  },
]

function haversine(lat1, lon1, lat2, lon2) {
  const R = 3958.8
  const dLat = (lat2 - lat1) * Math.PI / 180
  const dLon = (lon2 - lon1) * Math.PI / 180
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

const STATUS_META = {
  Researching:      { colClass: 'col-researching', dotClass: 'status-researching-dot' },
  Contacted:        { colClass: 'col-contacted',   dotClass: 'status-contacted-dot'   },
  Visited:          { colClass: 'col-visited',      dotClass: 'status-visited-dot'     },
  'Offer Received': { colClass: 'col-offer',        dotClass: 'status-offer-dot'       },
  Committed:        { colClass: 'col-committed',    dotClass: 'status-committed-dot'   },
}

const EMPTY_FORM = {
  school_name: '',
  division: 'D1',
  sport: 'Baseball',
  status: 'Researching',
  notes: '',
}

export default function Pipeline({ user }) {
  useEffect(() => { document.title = 'Pipeline — CommitHQ' }, [])

  const [schools, setSchools]           = useState([])
  const [loading, setLoading]           = useState(true)
  const [detailSchool, setDetailSchool] = useState(null)
  const [showModal, setShowModal]       = useState(false)
  const [form, setForm]                 = useState(EMPTY_FORM)
  const [saving, setSaving]             = useState(false)
  const [formError, setFormError]       = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [deleting, setDeleting]         = useState(false)

  // Notes
  const [openNotesId, setOpenNotesId]     = useState(null)
  const [localNotes, setLocalNotes]       = useState({})
  const [notesSavedId, setNotesSavedId]   = useState(null)
  const notesTimers                       = useRef({})

  // Card move overlay (click-to-move)
  const [openMoveCardId, setOpenMoveCardId] = useState(null)

  // Filters
  const [filterInterest, setFilterInterest] = useState('All')
  const [filterContact, setFilterContact]   = useState('All')
  const [filterMiles, setFilterMiles]       = useState('All')
  const [userLat, setUserLat]               = useState(null)
  const [userLng, setUserLng]               = useState(null)

  // Mobile column switcher
  const [activeMobileCol, setActiveMobileCol] = useState(STATUSES[0])

  // Drag and drop
  const [draggingId, setDraggingId]       = useState(null)
  const [dragOverStatus, setDragOverStatus] = useState(null)

  // Add School modal search
  const [schoolQuery, setSchoolQuery]         = useState('')
  const [searchResults, setSearchResults]     = useState([])
  const [searching, setSearching]             = useState(false)
  const [selectedSchool, setSelectedSchool]   = useState(null)
  const searchRef                             = useRef(null)

  // ── Data fetching ─────────────────────────────────────────────────

  const fetchSchools = useCallback(async () => {
    setLoading(true)
    const { data, error } = await supabase
      .from('pipeline_schools')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
    if (!error && data) {
      // Merge lat/lng/state from schools_db onto each pipeline school
      const schoolNames = data.map(s => s.school_name)
      const { data: geoData } = await supabase
        .from('schools_db')
        .select('school_name, lat, lng, state')
        .in('school_name', schoolNames)
      const geoMap = {}
      if (geoData) geoData.forEach(g => { geoMap[g.school_name] = { lat: g.lat, lng: g.lng, state: g.state } })
      const merged = data.map(s => ({ ...s, ...(geoMap[s.school_name] ?? {}) }))
      setSchools(merged)
    }
    setLoading(false)
  }, [user.id])

  useEffect(() => { fetchSchools() }, [fetchSchools])

  // ── Load user home coords from profiles ──────────────────────────

  useEffect(() => {
    async function loadUserCoords() {
      const { data } = await supabase
        .from('profiles').select('*').eq('id', user.id).single()
      console.log('[Pipeline] home_lat:', data?.home_lat, 'home_lng:', data?.home_lng)
      if (data?.home_lat && data?.home_lng) {
        setUserLat(parseFloat(data.home_lat))
        setUserLng(parseFloat(data.home_lng))
      }
    }
    loadUserCoords()
  }, [user.id])

  // ── School search (modal) ─────────────────────────────────────────

  useEffect(() => {
    if (!schoolQuery.trim() || selectedSchool) { setSearchResults([]); return }
    const timer = setTimeout(async () => {
      setSearching(true)
      const { data } = await supabase
        .from('schools_db')
        .select('id, school_name, division')
        .ilike('school_name', `%${schoolQuery.trim()}%`)
        .limit(8)
      setSearchResults(data || [])
      setSearching(false)
    }, 250)
    return () => clearTimeout(timer)
  }, [schoolQuery, selectedSchool])

  // ── Close move overlay on outside click ───────────────────────────

  useEffect(() => {
    if (!openMoveCardId) return
    function handleClick(e) {
      if (!e.target.closest('.card-move-overlay')) setOpenMoveCardId(null)
    }
    document.addEventListener('click', handleClick)
    return () => document.removeEventListener('click', handleClick)
  }, [openMoveCardId])

  // ── Handlers ─────────────────────────────────────────────────────

  function selectSchool(s) {
    setSelectedSchool(s)
    setSchoolQuery(s.school_name)
    setForm(prev => ({ ...prev, school_name: s.school_name, division: s.division || prev.division }))
    setSearchResults([])
  }

  function clearSchool() {
    setSelectedSchool(null)
    setSchoolQuery('')
    setForm(prev => ({ ...prev, school_name: '', division: 'D1' }))
    setTimeout(() => searchRef.current?.focus(), 0)
  }

  function handleSearchBlur() { setTimeout(() => setSearchResults([]), 150) }

  function openModal() {
    setForm(EMPTY_FORM); setFormError(null)
    setSchoolQuery(''); setSearchResults([]); setSelectedSchool(null)
    setShowModal(true)
  }

  function closeModal() { setShowModal(false); setFormError(null) }

  function handleChange(e) { setForm(prev => ({ ...prev, [e.target.name]: e.target.value })) }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!selectedSchool) { setFormError('Please select a school from the search results.'); return }
    setSaving(true)
    const { error } = await supabase.from('pipeline_schools').insert({ ...form, user_id: user.id })
    if (error) { setFormError(error.message) } else { closeModal(); fetchSchools() }
    setSaving(false)
  }

  async function handleDeleteConfirm() {
    if (!deleteTarget) return
    setDeleting(true)
    const { error } = await supabase
      .from('pipeline_schools').delete()
      .eq('id', deleteTarget.id).eq('user_id', user.id)
    if (!error) setSchools(prev => prev.filter(s => s.id !== deleteTarget.id))
    setDeleteTarget(null)
    setDeleting(false)
  }

  async function handleMove(schoolId, newStatus) {
    setOpenMoveCardId(null)
    setSchools(prev => prev.map(s =>
      String(s.id) === String(schoolId) ? { ...s, status: newStatus } : s
    ))
    await supabase
      .from('pipeline_schools').update({ status: newStatus })
      .eq('id', schoolId).eq('user_id', user.id)
  }

  function toggleNotes(id, currentNotes) {
    if (openNotesId === id) {
      setOpenNotesId(null)
    } else {
      setOpenNotesId(id)
      setLocalNotes(prev => ({
        ...prev,
        [id]: prev[id] !== undefined ? prev[id] : (currentNotes ?? ''),
      }))
    }
  }

  function handleNotesChange(id, value) {
    setLocalNotes(prev => ({ ...prev, [id]: value }))
    clearTimeout(notesTimers.current[id])
    notesTimers.current[id] = setTimeout(async () => {
      await supabase
        .from('pipeline_schools').update({ notes: value })
        .eq('id', id).eq('user_id', user.id)
      setSchools(prev => prev.map(s => s.id === id ? { ...s, notes: value } : s))
      setNotesSavedId(id)
      setTimeout(() => setNotesSavedId(prev => prev === id ? null : prev), 2000)
    }, 1000)
  }

  // ── Drag and drop ─────────────────────────────────────────────────

  function handleDragStart(e, school) {
    e.dataTransfer.setData('schoolId', String(school.id))
    e.dataTransfer.effectAllowed = 'move'
    // Brief timeout so the browser captures the full-opacity card as the drag image
    setTimeout(() => setDraggingId(school.id), 0)
  }

  function handleDragEnd() {
    setDraggingId(null)
    setDragOverStatus(null)
  }

  function handleDragOver(e, status) {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (dragOverStatus !== status) setDragOverStatus(status)
  }

  function handleDrop(e, targetStatus) {
    e.preventDefault()
    const schoolId = e.dataTransfer.getData('schoolId')
    if (schoolId) {
      const school = schools.find(s => String(s.id) === schoolId)
      if (school && school.status !== targetStatus) handleMove(schoolId, targetStatus)
    }
    setDragOverStatus(null)
    setDraggingId(null)
  }

  // ── Filter logic ──────────────────────────────────────────────────

  const anyFilterActive = filterInterest !== 'All' || filterContact !== 'All' || filterMiles !== 'All'

  const filteredSchools = schools.filter(school => {
    if (filterInterest !== 'All' && school.my_interest !== filterInterest) return false
    if (filterContact !== 'All' && school.contact_level !== filterContact) return false
    if (filterMiles !== 'All') {
      if (!school.lat || !school.lng || userLat === null || userLng === null) return false
      const dist = haversine(userLat, userLng, school.lat, school.lng)
      const opt = MILES_OPTIONS.find(o => o.label === filterMiles)
      if (opt) {
        if (opt.max !== null && dist >= opt.max) return false
        if (opt.min !== null && dist < opt.min) return false
      }
    }
    return true
  })

  const filteredCount = schools.length - filteredSchools.length

  function clearFilters() {
    setFilterInterest('All')
    setFilterContact('All')
    setFilterMiles('All')
  }

  // ── SchoolDetail drill-down ───────────────────────────────────────

  if (detailSchool) {
    return (
      <SchoolDetail
        pipelineSchool={detailSchool}
        user={user}
        onBack={() => { setDetailSchool(null); fetchSchools() }}
      />
    )
  }

  // ── Render ────────────────────────────────────────────────────────

  // First card across all columns — used for tour spotlight
  const firstCardId = (() => {
    for (const status of STATUSES) {
      const col = filteredSchools.filter(s => s.status === status)
      if (col.length > 0) return col[0].id
    }
    return null
  })()

  return (
    <div className="pipeline">

      {/* Toolbar */}
      <div className="pipeline-toolbar">
        <div className="pipeline-count">
          {!loading && (
            <span>{schools.length} {schools.length === 1 ? 'school' : 'schools'} tracked</span>
          )}
        </div>
        <button className="btn-add-school" onClick={openModal} data-tour="add-school">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          Add School
        </button>
      </div>

      {/* Filter bar */}
      <div className="pipeline-filters">
        <div className="pipeline-filters-label">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
          </svg>
          Filters
        </div>
        <div className="pipeline-filters-controls">
          <select
            className="pipeline-filter-select"
            value={filterInterest}
            onChange={e => setFilterInterest(e.target.value)}
          >
            <option value="All">All Interests</option>
            {MY_INTEREST_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
          <select
            className="pipeline-filter-select"
            value={filterContact}
            onChange={e => setFilterContact(e.target.value)}
          >
            <option value="All">All Contact Levels</option>
            {CONTACT_LEVEL_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
          <select
            className="pipeline-filter-select"
            value={filterMiles}
            onChange={e => setFilterMiles(e.target.value)}
          >
            <option value="All">All Distances</option>
            {MILES_OPTIONS.map(o => <option key={o.label} value={o.label}>{o.label}</option>)}
          </select>
        </div>
      </div>

      {/* Filter status */}
      {anyFilterActive && (
        <div className="pipeline-filter-status">
          <span>{filteredCount} {filteredCount === 1 ? 'result' : 'results'} filtered out</span>
          <button className="pipeline-filter-clear" onClick={clearFilters}>Clear Filters</button>
        </div>
      )}

      {/* Mobile column tabs */}
      <div className="pipeline-mobile-tabs">
        {STATUSES.map(status => {
          const count = filteredSchools.filter(s => s.status === status).length
          const meta  = STATUS_META[status]
          return (
            <button
              key={status}
              className={`pmobile-tab ${meta.colClass}${activeMobileCol === status ? ' active' : ''}`}
              onClick={() => setActiveMobileCol(status)}
            >
              <span className="pmobile-tab-name">{status}</span>
              <span className="pmobile-tab-count">{count}</span>
            </button>
          )
        })}
      </div>

      {/* Board */}
      {loading ? (
        <div className="pipeline-loading">Loading your pipeline…</div>
      ) : (
        <div className="kanban-board">
          {STATUSES.map(status => {
            const col  = filteredSchools.filter(s => s.status === status)
            const meta = STATUS_META[status]
            const isOver = dragOverStatus === status

            return (
              <div
                key={status}
                className={`kanban-col${isOver ? ' drag-over' : ''}${activeMobileCol === status ? ' mobile-active' : ''}`}
                onDragOver={e => handleDragOver(e, status)}
                onDrop={e => handleDrop(e, status)}
              >
                <div
                  className={`kanban-col-header ${meta.colClass}`}
                  data-tour={status === 'Researching' ? 'col-researching' : undefined}
                >
                  <span className="kanban-col-name">{status}</span>
                  <span className="kanban-col-count">{col.length}</span>
                </div>

                <div className="kanban-col-body">
                  {col.length === 0 ? (
                    <div className="kanban-col-empty">No schools yet</div>
                  ) : (
                    col.map(school => {
                      const isMoveOpen = openMoveCardId === school.id

                      return (
                        <div
                          key={school.id}
                          className={`kanban-card${draggingId === school.id ? ' dragging' : ''}${isMoveOpen ? ' move-mode' : ''}`}
                          draggable
                          onDragStart={e => handleDragStart(e, school)}
                          onDragEnd={handleDragEnd}
                          onClick={() => { if (!isMoveOpen) setDetailSchool(school) }}
                          data-tour={school.id === firstCardId ? 'first-card' : undefined}
                        >
                          {isMoveOpen ? (
                            /* ── Move overlay ── */
                            <div className="card-move-overlay" onClick={e => e.stopPropagation()}>
                              <p className="card-move-label">Move to:</p>
                              <div className="card-move-buttons">
                                {STATUSES.map(s => (
                                  <button
                                    key={s}
                                    className={`card-move-btn ${STATUS_META[s].colClass}${s === school.status ? ' current' : ''}`}
                                    onClick={() => {
                                      if (s !== school.status) handleMove(school.id, s)
                                      else setOpenMoveCardId(null)
                                    }}
                                  >
                                    <span className={`status-dot ${STATUS_META[s].dotClass}`} />
                                    {s}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <>
                              {/* Name + delete */}
                              <div className="kanban-card-top">
                                <span className="kanban-card-name">{school.school_name}</span>
                                <button
                                  className="btn-card-delete"
                                  onClick={e => { e.stopPropagation(); setDeleteTarget(school) }}
                                  aria-label={`Remove ${school.school_name}`}
                                >
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                    <polyline points="3 6 5 6 21 6"/>
                                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                                    <path d="M10 11v6"/><path d="M14 11v6"/>
                                    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                                  </svg>
                                </button>
                              </div>

                              {/* Division */}
                              {school.division && (
                                <div className="kanban-card-meta">
                                  <span className="division-badge">{school.division}</span>
                                </div>
                              )}

                              {/* Notes preview */}
                              {school.notes && openNotesId !== school.id && (
                                <p className="kanban-card-notes-preview">
                                  {school.notes.length > 60
                                    ? school.notes.slice(0, 60) + '…'
                                    : school.notes}
                                </p>
                              )}

                              {/* Notes editor */}
                              {openNotesId === school.id && (
                                <div
                                  className="kanban-notes-expand"
                                  onDragStart={e => e.stopPropagation()}
                                  onClick={e => e.stopPropagation()}
                                >
                                  <textarea
                                    className="kanban-notes-textarea"
                                    value={localNotes[school.id] ?? ''}
                                    onChange={e => handleNotesChange(school.id, e.target.value)}
                                    placeholder="Add notes about this school…"
                                    rows={3}
                                    autoFocus
                                  />
                                  {notesSavedId === school.id && (
                                    <span className="kanban-notes-saved">Saved ✓</span>
                                  )}
                                </div>
                              )}

                              {/* Footer */}
                              <div className="kanban-card-footer" onClick={e => e.stopPropagation()}>
                                <button
                                  className={`btn-card-notes${openNotesId === school.id ? ' active' : ''}`}
                                  onClick={() => toggleNotes(school.id, school.notes)}
                                >
                                  {openNotesId === school.id ? (
                                    <>
                                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                        <polyline points="18 15 12 9 6 15"/>
                                      </svg>
                                      Close
                                    </>
                                  ) : (
                                    <>
                                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                      </svg>
                                      Notes
                                    </>
                                  )}
                                </button>

                                <button
                                  className="btn-card-move"
                                  onClick={() => setOpenMoveCardId(school.id)}
                                >
                                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="5 9 2 12 5 15"/>
                                    <polyline points="19 9 22 12 19 15"/>
                                    <line x1="2" y1="12" x2="22" y2="12"/>
                                  </svg>
                                  Move
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      )
                    })
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Delete confirm modal */}
      {deleteTarget && (
        <div className="modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="modal modal-confirm" onClick={e => e.stopPropagation()}>
            <div className="modal-confirm-body">
              <div className="modal-confirm-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                  <polyline points="3 6 5 6 21 6"/>
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                  <path d="M10 11v6"/><path d="M14 11v6"/>
                  <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                </svg>
              </div>
              <p className="modal-confirm-text">
                Remove <strong>{deleteTarget.school_name}</strong> from your pipeline?
              </p>
              <p className="modal-confirm-subtext">This will not affect any coaches or data in the CommitHQ database.</p>
            </div>
            <div className="modal-confirm-actions">
              <button className="btn-cancel" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</button>
              <button className="btn-remove" onClick={handleDeleteConfirm} disabled={deleting}>
                {deleting ? 'Removing…' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add School modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add School</h3>
              <button className="modal-close" onClick={closeModal} aria-label="Close">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <form className="modal-form" onSubmit={handleSubmit}>
              <div className="modal-field">
                <label htmlFor="school_search">School Name <span className="required">*</span></label>
                <div className="school-search-wrap">
                  {selectedSchool ? (
                    <div className="school-selected">
                      <span className="school-selected-name">{selectedSchool.school_name}</span>
                      <button type="button" className="school-clear" onClick={clearSchool} aria-label="Clear selection">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                          <line x1="18" y1="6" x2="6" y2="18"/>
                          <line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <div className="school-search-input-wrap">
                      <svg className="search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <circle cx="11" cy="11" r="8"/>
                        <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                      </svg>
                      <input
                        ref={searchRef}
                        id="school_search"
                        type="text"
                        placeholder="Search for a school…"
                        value={schoolQuery}
                        onChange={e => setSchoolQuery(e.target.value)}
                        onBlur={handleSearchBlur}
                        autoFocus
                        autoComplete="off"
                      />
                      {searching && <span className="search-spinner" />}
                    </div>
                  )}
                  {searchResults.length > 0 && (
                    <ul className="school-dropdown">
                      {searchResults.map(result => (
                        <li key={result.id} className="school-dropdown-item" onMouseDown={() => selectSchool(result)}>
                          <span className="dropdown-school-name">{result.school_name}</span>
                          {result.division && <span className="dropdown-division-badge">{result.division}</span>}
                        </li>
                      ))}
                    </ul>
                  )}
                  {!selectedSchool && schoolQuery.trim().length > 1 && !searching && searchResults.length === 0 && (
                    <div className="school-no-results">No schools found for "{schoolQuery}"</div>
                  )}
                </div>
              </div>
              <div className="modal-field">
                <label htmlFor="status">Status</label>
                <select id="status" name="status" value={form.status} onChange={handleChange}>
                  {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="modal-field">
                <label htmlFor="notes">Notes</label>
                <textarea
                  id="notes" name="notes"
                  placeholder="Any notes about this school…"
                  value={form.notes} onChange={handleChange} rows={3}
                />
              </div>
              {formError && <div className="modal-error">{formError}</div>}
              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn-save" disabled={saving}>{saving ? 'Saving…' : 'Add School'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
